import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import BookmarkButton from "@/components/BookmarkButton";
import { GithubMark } from "@/components/GithubBits";
import { useSkillPath } from "@/lib/skillPath";
import type { Skill } from "@/lib/types";

const STATUS_CLS: Record<string, string> = {
  active:   "bg-green-500/20 text-green-400",
  pending:  "bg-yellow-500/20 text-yellow-300",
  rejected: "bg-red-500/20 text-red-400",
};

// Keep cards compact: show at most the first 50 characters of the summary, then an ellipsis.
const SUMMARY_MAX = 50;
function shortSummary(summary: string | null | undefined): string {
  const s = (summary || "").trim();
  if (!s) return "No summary.";
  return s.length > SUMMARY_MAX ? `${s.slice(0, SUMMARY_MAX).trimEnd()}…` : s;
}

// User-facing status labels — a skill still in the Scan → Security pipeline reads as
// "processing" on the dashboard.
const STATUS_LABEL: Record<string, string> = {
  active:   "active",
  pending:  "processing",
  rejected: "archived",
};

// Compact "time since" for the age tag (e.g. archived 3d ago). Used by the Archives/Rejected
// dashboard sections (`showAge`) — deliberately terser than `timeAgo()` below, which is the
// card-footer "published N ago" readout on regular (non-minimal) cards.
function relAge(iso: string): string {
  const ms = Date.now() - new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z").getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// "3d ago" / "6mo ago" — the card footer's "last updated" timestamp on regular cards. Normalizes a
// naive-UTC string (no offset) to UTC before parsing, so the elapsed time isn't skewed by the
// viewer's timezone.
function timeAgo(dateStr: string): string {
  const iso = /[Zz]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : dateStr + "Z";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return "just published";
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// Design-system chip patterns (mono, uppercase, small) — "owned" (orange) is used for tags and
// the security-score readout; "neutral" (gray) is reserved for status/visibility labels.
const chipOwned = "inline-flex items-center rounded-[3px] border border-primary/25 bg-primary/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-primary";
const chipScore = chipOwned + " gap-1.5 px-2.5 py-1";

function ShieldIcon({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
// "Verified by SkillPatch" (security review passed) — a shield with a check mark, orange.
function ShieldCheckIcon({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" />
    </svg>
  );
}
// "Official" (curated / publisher-imported, layer 0) — a rosette/seal shape, blue. Deliberately
// a different silhouette from ShieldCheckIcon so the two badges never look interchangeable.
function BadgeCheckIcon({ className = "h-3 w-3" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l2.24 2.09 3.02-.53.53 3.02L20.9 8.5l-1.4 2.76 1.4 2.76-2.11 1.92-.53 3.02-3.02-.53L12 21.02l-2.24-2.09-3.02.53-.53-3.02L3.1 15.02l1.4-2.76-1.4-2.76 2.11-1.92.53-3.02 3.02.53z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

/** Extract GitHub org name from original_source_url.
 *  e.g. "https://github.com/veniceai/skills/tree/..." -> "veniceai"
 *  Returns null if URL is missing or can't be parsed.
 */
export function getGithubOrg(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const path = new URL(url).pathname; // "/veniceai/skills/tree/..."
    const parts = path.split("/").filter(Boolean);
    return parts[0] ?? null; // "veniceai"
  } catch {
    return null;
  }
}

/** Get avatar URL for a GitHub org. */
export function getOrgAvatarUrl(org: string): string {
  return `https://github.com/${org}.png?size=64`;
}

/** Get initials from a display name for fallback avatar. */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** Publisher avatar — shows GitHub logo or initials fallback. */
function PublisherAvatar({ org, name, size = 16 }: { org: string | null; name: string; size?: number }) {
  const [imgFailed, setImgFailed] = useState(false);

  if (!org || imgFailed) {
    return (
      <span
        className="flex shrink-0 items-center justify-center rounded-full bg-white/10 text-[8px] font-bold text-white/60"
        style={{ width: size, height: size }}
      >
        {getInitials(name)}
      </span>
    );
  }

  return (
    <img
      src={getOrgAvatarUrl(org)}
      alt={name}
      className="shrink-0 rounded-full"
      style={{ width: size, height: size }}
      onError={() => setImgFailed(true)}
    />
  );
}

function CardBody({ skill, showStatus, showAge, minimal, onBookmarkChange }: { skill: Skill; showStatus?: boolean; showAge?: boolean; minimal?: boolean; onBookmarkChange?: (bookmarked: boolean) => void }) {
  const org = getGithubOrg(skill.original_source_url);
  const isOfficial = skill.layer === 0;

  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <h3 className="truncate font-display font-semibold first-letter:uppercase">{skill.name}</h3>
          {skill.verified && (
            <span title="Verified by SkillPatch — passed our security review" className="shrink-0 text-primary">
              <ShieldCheckIcon className="h-3.5 w-3.5" />
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {skill.github_linked && (
            <span title="Linked to GitHub" className="text-text/35">
              <GithubMark size={13} />
            </span>
          )}
          {skill.rating.count > 0 && (
            <span className="text-sm text-accent" title={`${skill.rating.count} rating${skill.rating.count === 1 ? "" : "s"}`}>
              ★ {skill.rating.avg.toFixed(1)}
            </span>
          )}
          {!minimal && (
            <BookmarkButton slug={skill.slug} bookmarked={skill.bookmarked} onChange={onBookmarkChange} className="pointer-events-auto" />
          )}
        </div>
      </div>

      {showStatus && (
        <div className="mt-1.5 flex items-center gap-1.5">
          {/* "active" is the normal, expected state for a live skill — not worth a badge. Only
              pending ("processing") and rejected ("archived") are worth calling out. */}
          {skill.status !== "active" && (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_CLS[skill.status] ?? "bg-white/10 text-text/50"}`}>
              {STATUS_LABEL[skill.status] ?? skill.status}
            </span>
          )}
          {!minimal && (
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] text-text/40 uppercase tracking-wide">
              {skill.visibility}
            </span>
          )}
          {showAge && (
            <span className="ml-auto text-[10px] text-text/40" title={new Date(skill.updated_at).toLocaleString()}>
              {relAge(skill.updated_at)}
            </span>
          )}
        </div>
      )}

      {!showStatus && skill.owner_name && (
        <div className="mt-1.5 flex items-center gap-1.5">
          {isOfficial && (
            <PublisherAvatar org={org} name={skill.owner_name} size={16} />
          )}
          {/* [profiles+follows] Link the author to their profile (owned skills only; official has no owner). */}
          {skill.owner_username ? (
            <Link
              to={`/u/${skill.owner_username}`}
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto relative z-10 text-xs transition-colors hover:text-primary hover:underline"
              style={{ color: "var(--text-muted)" }}
            >
              by {skill.owner_name}
            </Link>
          ) : (
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              by {skill.owner_name}
            </p>
          )}
          {isOfficial && (
            <span title="Official — curated from a publisher's own repository" className="flex items-center gap-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-blue-400">
              <BadgeCheckIcon className="h-2.5 w-2.5" />
              Official
            </span>
          )}
          {skill.is_default && (
            <span title="Default — your admin gives this skill to every account (read-only)" className="rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              Default
            </span>
          )}
        </div>
      )}

      <p className="mt-2.5 truncate text-sm" style={{ color: "var(--text-secondary)" }}>{shortSummary(skill.summary)}</p>

      {skill.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {skill.tags.slice(0, 4).map((t) => (
            <span key={t} className={chipOwned}>{t}</span>
          ))}
        </div>
      )}

      {!minimal && skill.security_score != null && (
        <div className="mt-3">
          <span className={chipScore} title="Security score — automated review of this skill">
            <ShieldIcon /> Score {skill.security_score}
          </span>
        </div>
      )}

      {!minimal && (
        <div className="mt-auto flex items-center gap-2 pt-3 text-xs" style={{ color: "var(--text-muted)" }}>
          {skill.fork_of && (
            <span className="rounded-full border border-white/15 bg-white/[0.06] px-2 py-0.5 text-[10px] text-text/40">
              forked{skill.fork_parent_name ? ` from ${skill.fork_parent_name}` : ""}
            </span>
          )}
          <span>↓ {skill.metrics.downloads}</span>
          {skill.fork_count > 0 && (
            <>
              <span className="text-lg leading-none opacity-30">·</span>
              <span>{skill.fork_count} {skill.fork_count === 1 ? "fork" : "forks"}</span>
            </>
          )}
          <span className="ml-auto text-text/35" title={new Date(skill.updated_at).toLocaleString()}>
            {timeAgo(skill.updated_at)}
          </span>
        </div>
      )}
    </>
  );
}

export default function SkillCard({
  skill,
  onOpen,
  showStatus,
  showAge,
  minimal,
  onBookmarkChange,
}: {
  skill: Skill;
  onOpen?: (slug: string) => void;
  showStatus?: boolean;
  showAge?: boolean;
  minimal?: boolean;
  onBookmarkChange?: (bookmarked: boolean) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const skillPath = useSkillPath();
  const location = useLocation();
  const isOfficial = skill.layer === 0;
  // Stamp where this card was clicked from (path + tab) so the skill page can show a contextual
  // back crumb ("My skills › …", "Trending › …") and return here on Back.
  const from = { pathname: location.pathname, search: location.search };

  const cardStyle: React.CSSProperties = {
    background: "var(--glass-strong)",
    border: `1px solid ${hovered ? "rgba(225,100,46,0.4)" : "var(--border)"}`,
    borderTopColor: hovered ? "rgba(225,100,46,0.4)" : "var(--border-light)",
    borderRadius: "var(--radius-lg)",
    boxShadow: hovered
      ? "0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(225,100,46,0.15)"
      : "var(--shadow-card)",
    transition: "border-color 0.15s, box-shadow 0.15s, transform 0.15s",
    transform: hovered ? "translateY(-2px)" : undefined,
  };

  // "Stretched link" layout: the whole-card link/button is an absolute overlay, and
  // the content sits above it with pointer-events disabled — so clicks fall through to
  // the overlay (navigate) EXCEPT on children that opt back in (the bookmark toggle).
  // This keeps interactive controls out of the anchor/button (valid HTML) so clicking
  // the bookmark saves in place instead of opening the skill.
  const overlay = onOpen ? (
    <button type="button" onClick={() => onOpen(skill.slug)} aria-label={skill.name} className="absolute inset-0" />
  ) : (
    <Link to={skillPath(skill.slug)} state={{ from }} aria-label={skill.name} className="absolute inset-0" />
  );

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        className={`pointer-events-none absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent to-transparent ${
          isOfficial ? "via-blue-400/35" : "via-primary/35"
        }`}
      />
      {overlay}
      <div className="pointer-events-none relative flex h-full flex-col p-5 text-left">
        <CardBody skill={skill} showStatus={showStatus} showAge={showAge} minimal={minimal} onBookmarkChange={onBookmarkChange} />
      </div>
    </div>
  );
}
